import React, { useState } from "react";
import { Play, ExternalLink, Instagram, Youtube } from "lucide-react";

/**
 * VideoEmbed — lazy-loaded video player card.
 *
 * Rendering strategy (matches user-approved plan):
 *   - YouTube: iframe embed (autoplay+mute-on-click to respect browser policies)
 *   - Instagram: iframe embed of /embed URL; falls back to thumbnail card that
 *     opens the IG URL in a new tab if the iframe fails to load
 *
 * Props:
 *   content   — social_content object (platform, embed_url, thumbnail_url, title, …)
 *   autoPlay  — start expanded with autoplay (used inside StyleFeed)
 *   aspect    — "9x16" (default, reels) | "16x9" | "4x5"
 *   onOpen    — optional callback when thumbnail is clicked (for view tracking)
 */
export default function VideoEmbed({ content, autoPlay = false, aspect = "9x16", onOpen, testid }) {
  const [playing, setPlaying] = useState(Boolean(autoPlay));
  const [iframeFailed, setIframeFailed] = useState(false);

  const aspectClass =
    aspect === "16x9" ? "aspect-video" : aspect === "4x5" ? "aspect-[4/5]" : "aspect-[9/16]";

  const PlatformIcon = content.platform === "instagram" ? Instagram : Youtube;

  const openExternal = () => {
    try {
      window.open(content.video_url, "_blank", "noopener,noreferrer");
    } catch {
      /* ignore */
    }
  };

  const startPlay = () => {
    setPlaying(true);
    onOpen?.();
  };

  // Instagram embed card fallback — click to open externally
  const needsCardFallback =
    (content.platform === "instagram" && iframeFailed) ||
    !content.embed_url;

  if (playing && !needsCardFallback) {
    // YouTube: append autoplay=1&mute=1 if not already there
    let src = content.embed_url;
    if (content.platform === "youtube" && !/[?&]autoplay=1/.test(src)) {
      src += (src.includes("?") ? "&" : "?") + "autoplay=1&mute=1";
    }
    return (
      <div
        className={`relative ${aspectClass} bg-black overflow-hidden w-full`}
        data-testid={testid || `video-embed-${content.id}`}
      >
        <iframe
          src={src}
          title={content.title}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
          onError={() => setIframeFailed(true)}
        />
      </div>
    );
  }

  // Thumbnail card with play button (pre-play state, or IG fallback)
  return (
    <div
      className={`relative ${aspectClass} bg-neutral-900 overflow-hidden cursor-pointer group w-full`}
      data-testid={testid || `video-card-${content.id}`}
      onClick={needsCardFallback ? openExternal : startPlay}
      role="button"
      aria-label={`Play ${content.title}`}
    >
      {content.thumbnail_url ? (
        <img
          src={content.thumbnail_url}
          alt={content.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/40">
          <PlatformIcon size={56} strokeWidth={1} />
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-white/90 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full text-[11px] uppercase tracking-wider">
        <PlatformIcon size={12} />
        {content.platform === "instagram" ? "Reel" : "Short"}
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-white/95 text-black flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
          <Play fill="currentColor" size={22} className="ml-0.5" />
        </div>
      </div>

      <div className="absolute bottom-3 left-3 right-3 text-white">
        <div className="font-display text-lg leading-tight line-clamp-2">{content.title}</div>
        {needsCardFallback && (
          <div className="flex items-center gap-1 text-xs text-white/70 mt-1">
            <ExternalLink size={11} /> Open on {content.platform === "instagram" ? "Instagram" : "YouTube"}
          </div>
        )}
      </div>
    </div>
  );
}
